import { Badge, Button, LearnMoreLink } from '@auctisync/ui';
import type { Tables } from '@auctisync/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

type Approval = Tables<'bidder_approvals'>;
type Auction = Pick<Tables<'auctions'>, 'id' | 'title' | 'requires_bidder_approval'>;

type StatusFilter = 'pending' | 'approved' | 'rejected' | 'revoked' | 'all';

export function AdminAuctionApprovals() {
  const { id } = useParams();
  const { session } = useSession();
  const [auction, setAuction] = useState<Auction | null>(null);
  const [rows, setRows] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [noteByRow, setNoteByRow] = useState<Map<string, string>>(new Map());

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    let query = supabase
      .from('bidder_approvals')
      .select('*')
      .eq('auction_id', id)
      .order('requested_at', { ascending: false });
    if (status !== 'all') query = query.eq('status', status);
    const { data, error: err } = await query;
    if (err) setError(err.message);
    else setRows(data ?? []);
    setLoading(false);
  }, [id, status]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('auctions')
        .select('id, title, requires_bidder_approval')
        .eq('id', id)
        .maybeSingle();
      if (!cancelled) setAuction(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleDecide(row: Approval, newStatus: 'approved' | 'rejected' | 'revoked') {
    if (!session) return;
    setBusyId(row.id);
    setError(null);
    const note = noteByRow.get(row.id)?.trim() ?? '';
    const { error: err } = await supabase
      .from('bidder_approvals')
      .update({
        status: newStatus,
        decided_at: new Date().toISOString(),
        decided_by: session.user.id,
        decision_note: note === '' ? null : note,
      })
      .eq('id', row.id);
    setBusyId(null);
    if (err) setError(err.message);
    else await refresh();
  }

  return (
    <div>
      <header style={{ marginBottom: '1.5rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <Link
            to={`/admin/auctions/${id}`}
            style={{ color: 'var(--brand-accent)', fontSize: '0.875rem' }}
          >
            ← {auction?.title ?? 'Auction'}
          </Link>
        </div>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.875rem',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '0.25rem',
          }}
        >
          Bidder approval requests
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '0.35rem' }}>
          Review pre-approval requests from bidders. Decisions apply to this auction only.
        </p>
        <LearnMoreLink helpSlug="requesting-approval" newTab>
          How bidders see this
        </LearnMoreLink>
        {auction && !auction.requires_bidder_approval && (
          <div
            style={{
              marginTop: '0.75rem',
              padding: '0.5rem 0.75rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              background: 'var(--surface-muted, #fef3c7)',
              border: '1px solid var(--admin-hairline, var(--border))',
              fontSize: '0.85rem',
              color: 'var(--text)',
            }}
          >
            This auction does not require bidder approval. Requests below were submitted before that
            was disabled or are manual.
          </div>
        )}
      </header>

      <div
        role="tablist"
        aria-label="Filter by status"
        style={{
          display: 'flex',
          gap: '0.35rem',
          padding: '0.25rem',
          marginBottom: '1rem',
          border: '1px solid var(--admin-hairline, var(--border))',
          borderRadius: 'var(--r-card, 0.5rem)',
          background: 'var(--surface-elev)',
          width: 'fit-content',
        }}
      >
        {(['pending', 'approved', 'rejected', 'revoked', 'all'] as StatusFilter[]).map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={status === s}
            onClick={() => setStatus(s)}
            style={{
              padding: '0.35rem 0.75rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: 'none',
              background: status === s ? 'var(--brand-primary)' : 'transparent',
              color: status === s ? 'var(--brand-primary-fg)' : 'var(--text)',
              fontSize: '0.85rem',
              fontWeight: status === s ? 600 : 500,
              cursor: 'pointer',
            }}
          >
            {s[0]!.toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: '0.75rem 1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-danger, #fef2f2)',
            color: 'var(--text-danger, #991b1b)',
            border: '1px solid var(--border-danger, #fecaca)',
            marginBottom: '1rem',
            fontSize: '0.875rem',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading requests…</div>
      ) : rows.length === 0 ? (
        <div
          style={{
            padding: '2.5rem 1rem',
            textAlign: 'center',
            color: 'var(--text-muted)',
            border: '1px dashed var(--border)',
            borderRadius: 'var(--r-card, 0.5rem)',
          }}
        >
          No {status === 'all' ? '' : status} approval requests.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {rows.map((row) => (
            <div
              key={row.id}
              style={{
                border: '1px solid var(--admin-hairline, var(--border))',
                borderRadius: 'var(--r-card, 0.5rem)',
                background: 'var(--surface-elev)',
                padding: '0.75rem 1rem',
                display: 'grid',
                gap: '0.5rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                }}
              >
                <div>
                  <code
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                      fontFamily: 'var(--font-mono, monospace)',
                    }}
                  >
                    user {row.user_id.slice(0, 8)}…
                  </code>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    Requested {new Date(row.requested_at).toLocaleString()}
                  </div>
                </div>
                <StatusBadge status={row.status} />
              </div>
              {row.request_note && (
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text)' }}>
                  <strong style={{ color: 'var(--text-muted)' }}>Note:</strong> {row.request_note}
                </p>
              )}
              {row.decision_note && row.status !== 'pending' && (
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  <strong>Decision note:</strong> {row.decision_note}
                </p>
              )}
              {row.status === 'pending' && (
                <>
                  <input
                    type="text"
                    placeholder="Optional note to bidder…"
                    value={noteByRow.get(row.id) ?? ''}
                    onChange={(e) =>
                      setNoteByRow((prev) => {
                        const next = new Map(prev);
                        next.set(row.id, e.target.value);
                        return next;
                      })
                    }
                    style={{
                      padding: '0.4rem 0.65rem',
                      border: '1px solid var(--admin-hairline, var(--border))',
                      borderRadius: 'var(--r-tile, 0.375rem)',
                      background: 'var(--surface)',
                      color: 'var(--text)',
                      fontSize: '0.85rem',
                    }}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={busyId === row.id}
                      onClick={() => {
                        void handleDecide(row, 'rejected');
                      }}
                    >
                      Reject
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyId === row.id}
                      onClick={() => {
                        void handleDecide(row, 'approved');
                      }}
                    >
                      {busyId === row.id ? 'Saving…' : 'Approve'}
                    </Button>
                  </div>
                </>
              )}
              {row.status === 'approved' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busyId === row.id}
                    onClick={() => {
                      void handleDecide(row, 'revoked');
                    }}
                  >
                    Revoke approval
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'approved') return <Badge variant="success">{status}</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}
